import React, { Component } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import {
  TextField,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
  DialogActions
} from '@material-ui/core';
import { config, endpoints } from '../../config';
import { setLocalizationInterceptor } from '../../utils/interceptors';
import { isValidEmail } from '../../utils/validations';
import { ILanguageStrings } from '../../utils/languages/languages';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Grid from '@material-ui/core/Grid';
import { EmailValidationCode } from './EmailValidationCode';
import { StepperConfig } from '../../types/StepperConfig.types';
import { UserStatus } from '../../types/UserInfo.types';

const stepperConfig = new StepperConfig();

interface EmailCheckProps {
  nextStep: Function;
  handleChange: Function;
  email?: string;
  handleLanguageChange: Function;
  stepList: string[];
  language: string;
  captchaSolved?: boolean;
  companyKey: string;
  companyName: string;
  languageStrings: ILanguageStrings;
  onStepRemove: Function;
  rkey: string;
  okey: string;
  portalId: string;
  contractId: string;
}

export interface IEmailVerifyResponse {
  addressKey: number;
  email: string;
  personKey?: any;
  supplierCompanyKey: any;
  valid: boolean;
  status: string;
  personName?: string;
  companyName?: string;
  workPhone?: string;
  companyAddress?: string;
  userName?: string;
}

export interface IPersonFetchResponse {
  emailAddress: string;
  status: string;
  userFlag: string;
  canUserRequestAccess: boolean;
  showNotInvitedPopup: boolean;
}

export default class EmailCheck extends Component<EmailCheckProps, {}> {
  state = {
    captchaSolved: this.props.captchaSolved ? this.props.captchaSolved : false,
    userAccounts: [
      {
        addressKey: 0,
        email: '',
        personKey: null,
        supplierCompanyKey: '',
        valid: true,
        status: 'New Account',
        personName: '',
        companyName: '',
        workPhone: '',
        companyAddress: '',
        userName: ''
      }
    ],
    email: this.props.email ? this.props.email : '',
    emailValidation: this.props.email ? isValidEmail(this.props.email) : false,
    submitting: false,
    submitted: false,
    isModalEmailExistsOpened: false,
    isUserNotInvited: false
  };

  componentDidMount() {
    setLocalizationInterceptor(this.props.language);
    if (this.props.rkey && this.props.rkey.length > 0) {
      axios
        .get(`${endpoints.PersonFetch}/fetchEmailByPersonKey?personKey=${this.props.rkey}`)
        .then(personData => {
          if (personData.data && personData.data.statusCode === 200) {
            const email = personData.data.response.emailAddress;
            const emailEvent = { target: { value: email } };
            this.onChangeEmail(emailEvent);
            this.setState({ email, emailValidation: true });
          }
        })
        .catch(err => {
          this.setState({ email: '' });
        });
    }
  }

  handlePersonKeyChange = (personKey: string | null) => {
    // add the personKey to UserInfo step
    const newEvent = {
      target: {
        value: personKey
      }
    };
    // adding personkey to userInfo state
    this.props.handleChange('userInfo', 'personKey')(newEvent);
  };

  handleUserAccountsChange = (userAccounts: IEmailVerifyResponse[]) => {
    // add status to UserInfo step
    const newEvent = {
      target: {
        value: userAccounts
      }
    };
    // adding status to userInfo state
    this.props.handleChange('userInfo', 'userAccounts')(newEvent);
  };

  /**
   * Handles all use cases for request for access
   * @param response The object with the response from the fetch user endpoint
   */
  handlePersonFetchResponse = (response: IPersonFetchResponse) => {
    const { canUserRequestAccess, status, userFlag, showNotInvitedPopup } = response;
    if (showNotInvitedPopup) {
      this.setState({ isUserNotInvited: true });
    }

    if (canUserRequestAccess) {
      if (userFlag === '' && status === '') {
        // console.log('first use case triggered');
        // first use case: new user
        // continue normal workflow, should it display an information modal window?
        this.setState({ submitted: true });
      } else if (status !== UserStatus.active || userFlag !== 'supplier') {
        // console.log('second use case triggered');
        // first use case: existing user with userFlag different than active or status other than supplier
        this.setState({ submitted: true });
        this.props.onStepRemove([stepperConfig.UserInfo, stepperConfig.Login]);
        // console.log('removing UserInfo and Login steps');
      } else {
        // existing user, do not ask for user info or login information
        this.setState({ submitted: true });
        this.props.onStepRemove([stepperConfig.UserInfo, stepperConfig.Login]);
        // console.log('removing UserInfo and Login steps');
        // console.log('canRequestUserAccess=true else triggered');
      }
    } else {
      if (status === UserStatus.active && userFlag === 'supplier') {
        // console.log('third use case triggered');
        // third use case: existing user with an access request or invite
        // display popup and force login
        this.setState({ isActiveAccountModalOpened: true });
      } else if (status === UserStatus.invited && userFlag === 'supplier') {
        // console.log('fourth use case triggered');
        // fourth use case: invited user
        this.props.onStepRemove(stepperConfig.Login);
        this.setState({ submitted: true });
        // console.log('removing Login step');
      } else {
        // console.log('canRequestUserAccess=false else triggered');
        // TODO: which steps should be removed?
        this.setState({ submitted: true });
      }
    }
  };

  handleRegister = (email: string) => {
    this.verifyExistingUser(this.props.rkey, this.props.okey, this.props.portalId, this.props.contractId, email);
    this.setState({ submitted: true });
  };

  /**
   * Registers an user and process the response
   */
  registerEmail = () => {
    const postData = {
      email: this.state.email,
      companyKey: this.props.companyKey,
      lang: this.props.language
    };
    this.setState({ submitting: true, submitted: false });
    axios
      .post(endpoints.RegisterEmail, postData)
      .then(res => {
        this.setState({ submitting: false });
        if (res.status === 201) {
          this.handleRegister(this.state.email);
        }
      })
      .catch(err => {
        if (err.response?.data && err.response?.data?.length > 0 && this.state.userAccounts.length <= 1) {
          this.handleRegister(this.state.email);
          err.response.data.sort((a: any, b: any) => {
            const statusA = a.status.toUpperCase();
            const statusB = b.status.toUpperCase();
            if (statusA < statusB) {
              return 1;
            }
            if (statusA > statusB) {
              return -1;
            }
            return 0;
          });
          this.state.userAccounts.unshift(...err.response.data);
          this.handleUserAccountsChange(this.state.userAccounts);
        }
        this.setState({ submitting: false, submitted: true });
      });
  };

  emailRequest = (
    endpoint: string,
    params: {
      emailAddress: string;
      rkey?: string;
      okey?: string;
    }
  ) =>
    axios
      .get(endpoint, { params })
      .then(res => {
        const { data }: { data: IPersonFetchResponse } = res;
        this.handlePersonFetchResponse(data);
        this.setState({ submitting: false });
      })
      .catch(() => {
        this.setState({ submitting: false });
      });

  /**
   * Runs request for access logic for an existing user
   * @param rkey The person key
   * @param okey The okey
   * @param emailAddress The email address of the user
   */
  verifyExistingUser = (rkey: string, okey: string, portalId: string, contractId: string, emailAddress: string) => {
    this.setState({ submitting: true, submitted: false });
    // fetch by person key if `rkey` is present. Otherwise, fetch by email
    if (portalId.trim().length > 0) {
      const newEvent = {
        target: {
          value: portalId
        }
      };
      this.props.handleChange('requestAccess', 'portalId')(newEvent);
    }
    if (contractId.trim().length > 0) {
      const newEvent = {
        target: {
          value: contractId
        }
      };
      this.props.handleChange('requestAccess', 'contractId')(newEvent);
    }
    if (rkey.trim().length > 0) {
      const newEvent = {
        target: {
          value: rkey
        }
      };
      this.props.handleChange('requestAccess', 'refPersonKey')(newEvent);
    }
    const endpointURL =
      rkey.trim().length > 0 ? `${endpoints.PersonFetch}/fetchByPersonKey` : `${endpoints.PersonFetch}/fetchByEmail`;
    // verify if user exists
    const params = { rkey: rkey || '', okey: okey || '', emailAddress };
    this.emailRequest(endpointURL, params);
  };

  continue = (e: { preventDefault: () => void; stopPropagation: () => void }) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.state.email) {
      this.registerEmail();
    }
  };

  onCaptchaResolved = (token: any) => {
    this.setState({ captchaSolved: true });
  };

  onCaptchaExpired = () => {
    this.setState({ captchaSolved: false });
  };

  onChangeEmail = (event: { target: { value: string } }) => {
    const email = event.target.value.toLowerCase();
    const emailValidation = isValidEmail(email);
    this.setState({
      email,
      emailValidation
    });

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: email
      }
    };

    this.props.handleChange('userInfo', 'emailAddress')(newEvent);
  };

  changeLanguage = (e: { target: any; preventDefault: () => void }) => {
    e.preventDefault();
    const language = e.target.value;
    this.props.handleLanguageChange(language);
    setLocalizationInterceptor(language);
  };

  resetSecurityCheck = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const newEvent = {
      target: {
        value: ''
      }
    };

    this.props.handleChange('userInfo', 'emailAddress')(newEvent);
    this.setState({ email: '', submitted: false, captchaSolved: false, emailValidation: false });
  };

  renderEmailValidation = (submitted: boolean) => {
    if (submitted) {
      return (
        <EmailValidationCode
          nextStep={this.props.nextStep}
          handleChange={this.props.handleChange}
          email={this.state.email}
          companyKey={this.props.companyKey}
          languageStrings={this.props.languageStrings}
          onCancel={this.resetSecurityCheck}
          language={this.props.language}
          userAccounts={this.state.userAccounts}
          onStepRemove={this.props.onStepRemove}
          okey={this.props.okey}
          rkey={this.props.rkey}
          contractId={this.props.contractId}
          portalId={this.props.portalId}
          stepList={this.props.stepList}
        />
      );
    } else {
      return '';
    }
  };

  renderEmailSecurityCheck = (submitted: boolean) => {
    if (!submitted) {
      return (
        <form onSubmit={this.continue}>
          <p style={{ height: '8px' }}>
            {this.props.languageStrings.formatString(this.props.languageStrings.emailCheck.welcome, {
              company: this.props.companyName
            })}
          </p>
          <Grid container justifyContent="center">
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="demo-simple-select-helper-label">
                  {this.props.languageStrings.emailCheck.languageLabel}
                </InputLabel>
                <Select
                  label={this.props.languageStrings.emailCheck.languageLabel}
                  value={this.props.language}
                  onChange={this.changeLanguage}
                >
                  <MenuItem value="en">English (U.S.)</MenuItem>
                  <MenuItem value="fr">Français (Canada)</MenuItem>
                  <MenuItem value="es">Español</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <br />
          <Grid container justifyContent="center">
            <Grid item xs={6} sm={4} md={3} lg={2}>
              <TextField
                size="medium"
                className="textfield"
                label={this.props.languageStrings.emailCheck.enterEmail}
                onChange={event => this.onChangeEmail(event)}
                error={this.state.emailValidation === false && this.state.email.length > 0}
                helperText={
                  this.state.emailValidation === false && this.state.email.length > 0
                    ? this.props.languageStrings.emailCheck.validEmail
                    : ''
                }
                variant="outlined"
                value={this.state.email}
                inputProps={{ 'data-testid': 'email' }}
              />
            </Grid>
          </Grid>
          <div className="captcha-wrapper">
            <div className="captcha-content">
              <ReCAPTCHA
                key={this.props.language}
                sitekey={config.captchaSiteKey}
                onChange={token => this.onCaptchaResolved(token)}
                onExpired={() => this.onCaptchaExpired()}
                hl={this.props.language}
              />
            </div>
          </div>

          <div className="Form-actions">
            <Button
              disabled={!this.state.emailValidation || !this.state.captchaSolved || this.state.submitting}
              variant="contained"
              color="primary"
              type="submit"
            >
              {this.state.submitting ? (
                <CircularProgress style={{ width: '24px', height: '24px' }} />
              ) : (
                <span>{this.props.languageStrings.emailValidationCode.sendCodeLabel}</span>
              )}
            </Button>
          </div>
        </form>
      );
    } else {
      return '';
    }
  };

  /**
   * User not invited text array has a placeholder {role}. This function replaces that placeholder dynamically.
   * @param paragraph The paragraph to format
   */
  getUserNotInvitedRoleText(paragraph: string) {
    const { okey, portalId, contractId } = this.props;
    let role = '';

    if (typeof okey === 'string' && okey.length) {
      role = this.props.languageStrings.requestAccess.eventCoordinator;
    }

    if (typeof portalId === 'string' && portalId.length) {
      role = this.props.languageStrings.requestAccess.portalOwner;
    }

    if (typeof contractId === 'string' && contractId.length) {
      role = this.props.languageStrings.requestAccess.contractOwner;
    }

    return this.props.languageStrings.formatString(paragraph, { role });
  }

  renderRequestAccessModals = () => {
    const modalNotInvitedTextLength = this.props.languageStrings.requestAccess.modalEmailNotInvitedText.length;
    return (
      <>
        <Dialog open={this.state.isUserNotInvited} onClose={() => this.setState({ isUserNotInvited: false })}>
          <DialogContent>
            {this.props.languageStrings.requestAccess.modalEmailNotInvitedText.map(
              (paragraph: string, index: number) => {
                if (index === modalNotInvitedTextLength - 1) {
                  // last paragraph has the {role} placeholder
                  return (
                    <React.Fragment key={`par-${index}`}>
                      <Typography align="center">{this.getUserNotInvitedRoleText(paragraph)}</Typography>
                      <br />
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={`par-${index}`}>
                    <Typography align="center">{paragraph}</Typography>
                    <br />
                  </React.Fragment>
                );
              }
            )}
          </DialogContent>
          <DialogActions>
            <Grid container direction="row" justifyContent="space-evenly">
              <Button
                onClick={() => {
                  this.setState({ isUserNotInvited: false, submitted: false });
                }}
                variant="outlined"
                color="primary"
              >
                {this.props.languageStrings.requestAccess.modalCancelButton}
              </Button>
              <Button
                onClick={() => {
                  this.setState({ isUserNotInvited: false });
                }}
                variant="contained"
                color="primary"
              >
                {this.props.languageStrings.requestAccess.modalOkButton}
              </Button>
            </Grid>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  render() {
    return (
      <div className="EmailCheck">
        <div className="EmailSecurityCheck">
          {this.renderEmailSecurityCheck(this.state.submitted)}
          {this.renderEmailValidation(this.state.submitted)}
          {this.renderRequestAccessModals()}
        </div>
      </div>
    );
  }
}
